/**
 * Stores > Inventory variance at /stores/hq/inventory.
 *
 * HQ per-store variance report: one row per item that has CounterPoint on-hand
 * on this store (or has ever been counted on this store). The grid itself
 * (view filter, search, sort, mobile stacking, row actions) is
 * `InventoryVariance` from `@ampersandbrands/ui`, shared with The Sweet Spot.
 * This file is the fetch, the HQ store picker, the XLSX export link, the link
 * to Variance History (Cockpit-only, does not move), the product-page link on
 * each SKU, and the notes/audit modal (also Cockpit-only: The Sweet Spot's
 * notes are a single free-text field, a different component, so the modal
 * itself stays local to each app and the shared grid only exposes the
 * callback that opens it).
 *
 * Gated by stores:hq. HQ picks a store before the grid loads (the api's
 * variance endpoint is per-store; the picker mirrors the daily-routine page).
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import {
  InventoryVariance as SharedInventoryVariance,
  PoPickerSheet,
  type InvVarianceRow,
} from '@ampersandbrands/ui';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { NotesModal } from '@/components/NotesModal';
import { formatDateTime } from '@/lib/dateUtils';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import {
  useAddItemNote,
  useCreateCountSession,
  useItemNotes,
  useVariance,
  useStorePos,
  varianceExportUrl,
  isNotAvailable,
  denverToday,
  type PoSummary,
  type VarianceView,
} from '@/lib/useStores';
import { StoresHeader, AccessDeniedCard, NotAvailableCard, ErrorCard } from './storesShared';
import {
  readInventoryStoreParam,
  hasInventoryStoreParam,
  readLastInventoryStore,
  writeLastInventoryStore,
} from './inventoryStoreParam';
import { InventoryStoreHeader } from './InventoryStoreHeader';

export function InventoryVariance() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.variance', user?.superAdmin);
  const navigate = useNavigate();

  // Store + view filter live in the URL so refresh preserves them. Sort and
  // search are owned by the shared grid itself (client-side, over the whole
  // per-store dataset the api returns in one response).
  const [searchParams, setSearchParams] = useSearchParams();

  const storeNo = readInventoryStoreParam(searchParams);
  const view = (['all', 'needs_count', 'variances'] as const).includes(searchParams.get('filter') as VarianceView)
    ? (searchParams.get('filter') as VarianceView)
    : 'variances';

  const updateParams = (mutate: (sp: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  };
  const setStoreNo = (n: number | null) => {
    updateParams((sp) => {
      if (n === null) {
        sp.delete('store');
        sp.delete('store_no');
      } else {
        sp.set('store', String(n));
        sp.delete('store_no');
      }
    });
    if (n !== null) writeLastInventoryStore(n);
  };
  const setView = (v: VarianceView) => updateParams((sp) => sp.set('filter', v));

  // Opening the page fresh (no store in the URL yet) reopens the last store
  // an HQ user picked anywhere in the inventory family (issue #507).
  useEffect(() => {
    if (storeNo !== null || hasInventoryStoreParam(searchParams)) return;
    const last = readLastInventoryStore();
    if (last !== null) setStoreNo(last);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filters = { ...(storeNo !== null ? { store_no: storeNo } : {}), filter: view };
  const varQ = useVariance(filters);

  const createSession = useCreateCountSession();
  const [pickingPo, setPickingPo] = useState(false);
  const posQ = useStorePos(storeNo ?? undefined, pickingPo);
  const [notesOpenFor, setNotesOpenFor] = useState<InvVarianceRow | null>(null);

  const sessionQs = storeNo !== null ? `?store=${storeNo}` : '';

  const startShelfCount = () => {
    if (storeNo === null) return;
    createSession.mutate(
      { kind: 'shelf', store_no: storeNo },
      { onSuccess: (d) => navigate(`/stores/inventory/${d.session.id}${sessionQs}`) },
    );
  };

  const startReceiving = (po: PoSummary) => {
    if (storeNo === null) return;
    createSession.mutate(
      { kind: 'receiving', po_no: po.po_no, store_no: storeNo },
      { onSuccess: (d) => navigate(`/stores/inventory/${d.session.id}${sessionQs}`) },
    );
  };

  if (user && !canView) return <AccessDeniedCard what="The inventory variance report" />;
  if (isNotAvailable(varQ.error)) {
    return (
      <div>
        <StoresHeader title="Inventory variance" />
        <NotAvailableCard what="The inventory variance report" />
      </div>
    );
  }

  const rows = varQ.data?.rows ?? [];
  const totals = varQ.data?.totals;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <InventoryStoreHeader
        title="Inventory variance"
        actions={<ToolPinToggle toolSlug="stores-hq-variance" toolName="Inventory variance" />}
        date={denverToday()}
        storeNo={storeNo}
        onStoreChange={setStoreNo}
      />

      <SharedInventoryVariance
        rows={rows}
        totals={totals}
        loading={varQ.isLoading}
        storeSelected={storeNo !== null}
        view={view}
        onViewChange={setView}
        onStartShelf={startShelfCount}
        onReceiveOrder={() => setPickingPo(true)}
        actionsPending={createSession.isPending}
        actionsError={createSession.error && <div className="px-3 pt-3"><ErrorCard message={createSession.error.message} /></div>}
        onOpenNotes={(r) => setNotesOpenFor(r)}
        renderSku={(r) => (
          <Link
            to={`/commerce/products/${encodeURIComponent(r.item_no)}?from=variance`}
            className="underline decoration-dotted underline-offset-2 hover:text-accent"
          >
            {r.item_no}
          </Link>
        )}
        toolbarExtra={
          storeNo !== null && (
            <a
              href={varianceExportUrl(filters)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-sm font-medium text-ink hover:border-accent"
            >
              <Download className="h-4 w-4" aria-hidden /> Export
            </a>
          )
        }
      />

      {pickingPo && storeNo !== null && (
        <PoPickerSheet
          open={pickingPo}
          pos={posQ.data?.pos ?? []}
          loading={posQ.isLoading}
          error={posQ.error && <ErrorCard message={posQ.error.message} />}
          onPick={(po) => {
            setPickingPo(false);
            startReceiving(po as PoSummary);
          }}
          onClose={() => setPickingPo(false)}
        />
      )}

      {notesOpenFor !== null && storeNo !== null && (
        <ItemNotesModal
          itemNo={notesOpenFor.item_no}
          itemDescr={notesOpenFor.descr}
          storeNo={storeNo}
          onClose={() => setNotesOpenFor(null)}
        />
      )}
    </div>
  );
}

// Item-scoped wrapper around the shared centered NotesModal (COCKPIT.md 2.10).
// A single GET returns notes + audit in one payload, so no lazy toggle: the
// modal only mounts when the operator opens it, and we fetch on mount.
function ItemNotesModal({
  itemNo,
  itemDescr,
  storeNo,
  onClose,
}: {
  itemNo: string;
  itemDescr: string | null;
  storeNo: number;
  onClose: () => void;
}) {
  const notesQ = useItemNotes(itemNo, storeNo);
  const addNote = useAddItemNote();

  const notes = (notesQ.data?.notes ?? []).map((n) => ({
    id: n.id,
    author: n.created_by_email,
    body: n.note,
    created_at: n.created_at,
  }));
  const audit = (notesQ.data?.audit ?? []).map((e) => ({
    id: e.id,
    actor: e.actor_email,
    action: e.action,
    outcome: e.outcome,
    detail: e.detail,
    occurred_at: e.ts,
  }));

  return (
    <NotesModal
      title={itemDescr ? `${itemNo} · ${itemDescr}` : itemNo}
      eyebrow="Item"
      notes={notes}
      notesLoading={notesQ.isLoading}
      notesError={notesQ.error?.message ?? null}
      audit={audit}
      auditLoading={notesQ.isLoading}
      auditError={notesQ.error?.message ?? null}
      canWrite
      adding={addNote.isPending}
      addError={addNote.error?.message ?? null}
      onAddNote={(note) => addNote.mutate({ item_no: itemNo, note, store_no: storeNo })}
      formatTimestamp={formatDateTime}
      onClose={onClose}
    />
  );
}
